/**
 * @file app/tauri/miniAppNative.web.js  (web / desktop)
 *
 * Native bridge + window chrome of the desktop mini apps (Happy Time Tracker /
 * Happy Team Chat). Wraps the `miniapp` Tauri commands from
 * `src-tauri/src/miniapp.rs` (`set_tray_title`, `set_dock_badge`,
 * `show_native_notification`) behind fail-soft helpers: in a plain browser, in
 * the full Happy App, or in a shell built without the `miniapp` feature every
 * call resolves to a no-op instead of throwing.
 *
 * `installMiniAppChrome()` gives the webview the look of a native app:
 *   • macOS uses `titleBarStyle: Overlay` (traffic lights float over the web
 *     content), so a 28px draggable strip is reserved at the top and the app is
 *     pushed down by the same amount. Its colour follows whatever the app paints
 *     right under it, so light / dark themes never show a seam.
 *   • Windows / Linux keep the native frame — no strip, no inset.
 *   • No browser-isms: no page-zoom, no rubber-band overscroll, no right-click
 *     "Reload / Inspect" menu in production, and a file dropped outside a drop
 *     zone no longer navigates the whole window away.
 *
 * webpack resolves `./miniAppNative` to THIS file (`.web.js` first); Metro
 * resolves it to the native no-op sibling.
 */

import {invoke} from '@tauri-apps/api/core';

/** Height of the macOS draggable strip, matching the `trafficLightPosition` in the overlays. */
export const MAC_TITLEBAR_INSET = 28;

const STRIP_ID = 'happy-mini-titlebar';
const STYLE_ID = 'happy-mini-chrome';

/** @returns {boolean} True when running inside a Tauri webview. */
export const isDesktopShell = () => (
    typeof window !== 'undefined' && (!!window.__TAURI_INTERNALS__ || !!window.__TAURI__)
);

const isMac = () => typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || navigator.userAgent || '');

/**
 * Call a mini-app command, swallowing "command not found" (shell built without
 * the `miniapp` feature) and every other native failure.
 * @param {string} command Tauri command name.
 * @param {object} [args] Command arguments.
 * @returns {Promise<void>}
 */
const call = async (command, args) => {
    if (!isDesktopShell()) {
        return;
    }
    try {
        await invoke(command, args);
    } catch (_error) {
        /* tray / badge are cosmetic — never break the app for them */
    }
};

/**
 * Menu-bar text (macOS) / tooltip (elsewhere). `null` clears it.
 * @param {string|null} title Text to show.
 * @returns {Promise<void>}
 */
export const setTrayTitle = (title) => call('set_tray_title', {title: title || null});

/**
 * Dock badge (macOS/Linux) / taskbar overlay dot (Windows). 0 clears it.
 * @param {number} count Unread count.
 * @returns {Promise<void>}
 */
export const setDockBadge = (count) => call('set_dock_badge', {count: Math.max(0, Math.floor(Number(count) || 0))});

/**
 * Native OS notification.
 * @param {string} title Notification title.
 * @param {string} body Notification body.
 * @returns {Promise<void>}
 */
export const notifyNative = (title, body) => call('show_native_notification', {title: String(title || ''), body: String(body || '')});

/** @returns {boolean} Whether the mini-app window currently has keyboard focus. */
export const isWindowFocused = () => typeof document === 'undefined' || document.hasFocus();

const CHROME_CSS = `
html, body { overscroll-behavior: none; -webkit-font-smoothing: antialiased; -webkit-text-size-adjust: 100%; }
body { -webkit-touch-callout: none; }
#${STRIP_ID} { position: fixed; top: 0; left: 0; right: 0; height: ${MAC_TITLEBAR_INSET}px; z-index: 2147483000;
  -webkit-user-select: none; user-select: none; cursor: default; }
html.happy-mini-mac #root { box-sizing: border-box; padding-top: ${MAC_TITLEBAR_INSET}px; }
`;

/**
 * Sample the colour the app paints under the strip so the inset is seamless.
 * @param {HTMLElement} strip The drag strip element.
 */
const syncStripColor = (strip) => {
    try {
        strip.style.pointerEvents = 'none';
        let node = document.elementFromPoint(Math.floor(window.innerWidth / 2), MAC_TITLEBAR_INSET + 2);
        strip.style.pointerEvents = '';
        while (node && node !== document.documentElement) {
            const color = window.getComputedStyle(node).backgroundColor;
            if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
                strip.style.backgroundColor = color;

                return;
            }
            node = node.parentElement;
        }
    } catch (_error) {
        strip.style.pointerEvents = '';
    }
};

/**
 * Install the native-app chrome. Idempotent; a no-op outside a Tauri shell.
 * @param {{name?: string}} [miniApp] Mini-app descriptor (window title).
 */
export const installMiniAppChrome = (miniApp = {}) => {
    if (!isDesktopShell() || typeof document === 'undefined' || document.getElementById(STYLE_ID)) {
        return;
    }

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CHROME_CSS;
    document.head.appendChild(style);

    if (miniApp.name) {
        document.title = miniApp.name;
    }

    if (isMac()) {
        document.documentElement.classList.add('happy-mini-mac');
        const strip = document.createElement('div');
        strip.id = STRIP_ID;
        strip.setAttribute('data-tauri-drag-region', '');
        document.body.appendChild(strip);
        syncStripColor(strip);
        window.setInterval(() => document.hasFocus() && syncStripColor(strip), 1500);
    }

    // Production only, so devtools stay reachable while developing.
    if (process.env.NODE_ENV === 'production') {
        window.addEventListener('contextmenu', (event) => {
            const tag = event.target && event.target.tagName;
            if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !(event.target && event.target.isContentEditable)) {
                event.preventDefault();
            }
        });
    }

    // Bubble phase: element-level drop zones (chat attachments) run first.
    ['dragover', 'drop'].forEach((type) => window.addEventListener(type, (event) => event.preventDefault()));
    // Page zoom is a browser behaviour, not an app one.
    window.addEventListener('wheel', (event) => event.ctrlKey && event.preventDefault(), {passive: false});
};
